import {
  type Node,
  type Edge,
  type OnNodesChange,
  applyNodeChanges,
  getConnectedEdges,
} from '@xyflow/react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as Y from 'yjs';
import throttle from 'lodash/throttle';
import { useInitializedSyncedProjectStore } from '../stores/syncedProjectStore';

// Utility to perform shallow equality check
const shallowEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 == null ||
    obj2 == null
  )
    return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) return false;
  }
  return true;
};

// Utility to strip `selected` from a node before syncing
const stripSelected = (node: Node): Node => {
  const { selected, ...rest } = node;
  return rest as Node;
};

// Utility to merge incoming node with local selected state
const mergeWithLocalSelected = (incomingNode: Node, localNodes: Node[]): Node => {
  const localNode = localNodes.find((n) => n.id === incomingNode.id);
  return {
    ...incomingNode,
    selected: localNode?.selected ?? false,
  };
};

// Generate a unique session ID
const SESSION_ID: string = uuidv4();
const draggingNodes = new Set<string>();
const lastLocalPosition = new Map<string, { x: number; y: number }>();
const resizingNodes = new Set<string>();
const lastLocalDimensions = new Map<string, { width: number; height: number }>();

export function initializeNodesStateSynced(ydoc: Y.Doc) {
  const nodesMap = ydoc.getMap<Node>('nodesMap');
  const edgesMap = ydoc.getMap<Edge>('edgesMap');
  const undoManager = new Y.UndoManager([nodesMap, edgesMap]);

  return function useNodesStateSynced(
    userId: string | null
  ): [
      Node[],
      React.Dispatch<React.SetStateAction<Node[]>>,
      (node: Node) => void,
      OnNodesChange,
      Y.UndoManager,
      (nodeId: string) => void, // startDrag
      (nodeId: string) => void, // stopDrag
      (node: Node) => void, // forceSyncNode
      (nodeId: string) => void, // startResize
      (nodeId: string) => void  // stopResize
    ] {
    if (!userId) {
      console.warn('useNodesStateSynced: userId is undefined, proceeding with session-based updates');
    }

    const [nodes, setNodes] = useState<Node[]>([]);
    const dragUpdateCount = useRef(0);
    const resizeUpdateCount = useRef(0);

    const startDrag = useCallback(
      (nodeId: string) => {
        draggingNodes.clear();
        draggingNodes.add(nodeId);
        dragUpdateCount.current = 0;
        const node = nodes.find((n) => n.id === nodeId);
        if (!node || !node.position) {
          console.error(`startDrag: node ${nodeId} not found or missing position`);
          return;
        }
        lastLocalPosition.set(nodeId, { x: node.position.x, y: node.position.y });
      },
      [nodes]
    );

    const stopDrag = useCallback(
      (nodeId: string) => {
        const node = nodes.find((n) => n.id === nodeId);
        const lastPos = lastLocalPosition.get(nodeId);
        if (!node || !lastPos) {
          console.error(`stopDrag: node ${nodeId} not found or missing last position`);
          return;
        }
        setNodes((prevNodes) =>
          prevNodes.map((n) =>
            n.id === nodeId ? { ...n, position: { x: lastPos.x, y: lastPos.y } } : n
          )
        );
        const nodeToSync = {
          ...stripSelected(node),
          position: { x: lastPos.x, y: lastPos.y },
        };
        ydoc.transact(() => {
          nodesMap.set(nodeId, nodeToSync);
        }, SESSION_ID);
        draggingNodes.delete(nodeId);
        lastLocalPosition.delete(nodeId);
      },
      [nodes, ydoc]
    );

    const forceSyncNode = useCallback(
      (node: Node) => {
        if (node && node.id) {
          const nodeToSync = stripSelected(node);
          ydoc.transact(() => {
            nodesMap.set(node.id, nodeToSync);
          }, SESSION_ID);
        }
      },
      [ydoc]
    );

    const startResize = useCallback(
      (nodeId: string) => {
        resizingNodes.clear();
        resizingNodes.add(nodeId);
        resizeUpdateCount.current = 0;
        const node = nodes.find((n) => n.id === nodeId);
        if (!node || !node.width || !node.height) {
          console.error(`startResize: node ${nodeId} not found or missing dimensions`);
          return;
        }
        lastLocalDimensions.set(nodeId, { width: node.width, height: node.height });
      },
      [nodes]
    );

    const stopResize = useCallback(
      (nodeId: string) => {
        const node = nodes.find((n) => n.id === nodeId);
        const lastDims = lastLocalDimensions.get(nodeId);
        if (!node || !lastDims) {
          console.error(`stopResize: node ${nodeId} not found or missing last dimensions`);
          return;
        }
        setNodes((prevNodes) =>
          prevNodes.map((n) =>
            n.id === nodeId ? { ...n, width: lastDims.width, height: lastDims.height } : n
          )
        );
        const nodeToSync = {
          ...stripSelected(node),
          width: lastDims.width,
          height: lastDims.height
        };
        ydoc.transact(() => {
          nodesMap.set(nodeId, nodeToSync);
        }, SESSION_ID);
        resizingNodes.delete(nodeId);
        lastLocalDimensions.delete(nodeId);
      },
      [nodes, ydoc]
    );

    const setNodesSynced = useCallback(
      (nodesOrUpdater: React.SetStateAction<Node[]>) => {
        const seen = new Set<string>();
        const next =
          typeof nodesOrUpdater === 'function'
            ? nodesOrUpdater([...nodesMap.values()])
            : nodesOrUpdater;

        ydoc.transact(() => {
          for (const node of next) {
            if (node && node.id && !seen.has(node.id)) {
              seen.add(node.id);
              if (!draggingNodes.has(node.id)) {
                const nodeToSync = stripSelected(node);
                const current = nodesMap.get(node.id);
                if (!current || !shallowEqual(current, nodeToSync)) {
                  nodesMap.set(node.id, nodeToSync);
                }
              }
            }
          }

          for (const node of nodesMap.values()) {
            if (node && node.id && !seen.has(node.id)) {
              nodesMap.delete(node.id);
            }
          }
        }, SESSION_ID);
      },
      [ydoc]
    );

    const setNodeSynced = useCallback(
      (node: Node) => {
        if (node && node.id && !draggingNodes.has(node.id)) {
          const current = nodesMap.get(node.id);
          const nodeToSync = stripSelected(node);
          ydoc.transact(() => {
            if (!current || !shallowEqual(current, nodeToSync)) {
              nodesMap.set(node.id, nodeToSync);
            }
          }, SESSION_ID);
        }
      },
      [ydoc]
    );

    const debouncedOnNodesChange = useCallback(
      (changes: Parameters<OnNodesChange>[0]) => {
        const isLocalDrag = changes.every(
          (change) =>
            change.type === 'position' &&
            draggingNodes.has((change as any).id)
        );

        const isLocalResize = changes.every(
          (change) =>
            change.type === 'dimensions' &&
            resizingNodes.has((change as any).id)
        );

        if (isLocalDrag) {
          dragUpdateCount.current += 1;
          setNodes((prevNodes) => {
            const updatedNodes = applyNodeChanges(changes, prevNodes);
            changes.forEach((change) => {
              if (change.type === 'position' && 'id' in change && change.position) {
                lastLocalPosition.set(change.id, {
                  x: change.position.x,
                  y: change.position.y,
                });
              }
            });
            return updatedNodes;
          });
          return;
        }

        if (isLocalResize) {
          resizeUpdateCount.current += 1;
          setNodes((prevNodes) => {
            const updatedNodes = applyNodeChanges(changes, prevNodes);
            changes.forEach((change) => {
              if (change.type === 'dimensions' && 'id' in change && change.dimensions) {
                lastLocalDimensions.set(change.id, {
                  width: change.dimensions.width,
                  height: change.dimensions.height,
                });
              }
            });
            return updatedNodes;
          });
          return;
        }

        const isDragRelated = changes.every(
          (change) =>
            change.type === 'position' ||
            (change.type === 'dimensions' && 'dragging' in change)
        );
        if (isDragRelated) {
          dragUpdateCount.current += 1;
          return;
        }

        throttle(
          () => {
            const affectedNodeIds = changes
              .filter((change) => 'id' in change)
              .map((change) => (change as any).id);
            const nodesTemp = Array.from(nodesMap.values()).filter(
              (node) => node !== undefined && affectedNodeIds.includes(node.id)
            );

            const nextNodes = applyNodeChanges(changes, nodesTemp);

            setNodes((prevNodes) => applyNodeChanges(changes, prevNodes));

            ydoc.transact(() => {
              for (const change of changes) {
                if (change.type === 'select') {
                  continue;
                }
                if (change.type === 'add') {
                  const nodeToSync = stripSelected(change.item);
                  nodesMap.set(change.item.id, nodeToSync);
                } else if (change.type === 'replace') {
                  const current = nodesMap.get(change.item.id);
                  const nodeToSync = stripSelected(change.item);
                  console.log('Replacing node:', change.item, changes)
                  if (!current || !shallowEqual(current, nodeToSync)) {
                    console.log(`onNodesChange: replacing node ${change.item.id}`);
                    nodesMap.set(change.item.id, nodeToSync);
                  }
                } else if (change.type === 'remove' && nodesMap.has(change.id)) {
                  const deletedNode = nodesMap.get(change.id)!;
                  const connectedEdges = getConnectedEdges(
                    [deletedNode],
                    [...edgesMap.values()].filter(
                      (edge) =>
                        edge &&
                        typeof edge === 'object' &&
                        'source' in edge &&
                        edge.source !== undefined &&
                        edge.source !== null
                    )
                  );

                  nodesMap.delete(change.id);
                  for (const edge of connectedEdges) {
                    edgesMap.delete(edge.id);
                  }
                } else if ('id' in change) {
                  const nodeId = (change as any).id;
                  const newNode = nextNodes.find((n) => n.id === nodeId);
                  const oldNode = nodesMap.get(nodeId);

                  if (newNode) {
                    const nodeToSync = stripSelected(newNode);
                    if (!oldNode || !shallowEqual(oldNode, nodeToSync)) {
                      nodesMap.set(nodeId, nodeToSync);
                    }
                  }
                }
              }
            }, SESSION_ID);
          },
          100,
          { leading: true, trailing: true }
        )();
      },
      [ydoc]
    );

    const onNodesChange: OnNodesChange = useCallback(
      (changes) => {
        debouncedOnNodesChange(changes);
      },
      [debouncedOnNodesChange]
    );

    useEffect(() => {
      const updateNodes = (event: Y.YMapEvent<Node>) => {
        const currNodes = Array.from(nodesMap.values()).filter(
          (node) => node !== undefined
        );

        setNodes((prevNodes) => {
          const mergedNodes = prevNodes.map((prevNode) => {
            const updatedNode = currNodes.find((n) => n.id === prevNode.id);
            if (!updatedNode) {
              return prevNode;
            }
            if (draggingNodes.has(prevNode.id)) {
              return {
                ...updatedNode,
                position: prevNode.position,
                selected: prevNode.selected ?? false,
              };
            }
            return mergeWithLocalSelected(updatedNode, prevNodes);
          });

          const newNodes = currNodes
            .filter((node) => !prevNodes.some((prev) => prev.id === node.id))
            .map((node) => mergeWithLocalSelected(node, prevNodes));

          const finalNodes = [...mergedNodes, ...newNodes];

          const isSameLength = prevNodes.length === finalNodes.length;
          const isSameContent = prevNodes.every((node, i) => {
            const finalNode = finalNodes[i];
            return (
              node?.id === finalNode?.id &&
              node?.selected === finalNode?.selected &&
              shallowEqual(stripSelected(node), stripSelected(finalNode))
            );
          });

          if (isSameLength && isSameContent) {
            return prevNodes;
          }

          return finalNodes;
        });
      };

      updateNodes({ keysChanged: new Set(), transaction: { origin: SESSION_ID } } as any);
      nodesMap.observe(updateNodes);
      return () => {
        nodesMap.unobserve(updateNodes);
      };
    }, [ydoc]);

    return [
      nodes,
      setNodesSynced,
      setNodeSynced,
      onNodesChange,
      undoManager,
      startDrag,
      stopDrag,
      forceSyncNode,
      startResize,
      stopResize
    ];
  };
}

export function useNodesStateSynced(userId: string | null) {
  const { useNodesStateSynced } = useInitializedSyncedProjectStore();
  return useNodesStateSynced(userId);
}